import { useQueryClient } from '@tanstack/react-query';
import { RotateCw, Wrench } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BrandLogo } from '@/components/ui/BrandLogo';
import { Button } from '@/components/ui/Button';
import { useShopContent } from '@/features/storefront/content/useContent';
import { STOREFRONT_BUTTON_STYLE, STOREFRONT_HEADING_STYLE } from '@/features/storefront/theme';
import {
  fetchPlatformNotices,
  syncMaintenanceFromNotices,
  type NoticeAudience,
} from '@/features/system/api/system';
import { getMaintenance, maintenanceTime, useMaintenance } from '@/features/system/maintenance';
import { useT } from '@/lib/i18n';

/**
 * The whole page while the platform is in FULL maintenance.
 *
 * Rendered by the dashboard and storefront layouts in place of their content -
 * never by the staff panel, which has to keep working so someone can end the
 * window. It asks `/meta/notices` again every 30 seconds (and on "Try again");
 * once maintenance is no longer active it clears the store, every query is
 * asked again, and the layout puts the app back - no reload, nothing typed
 * lost from memory that was not already lost.
 *
 * Deliberately not checked on mount: a refusal the notices endpoint has not
 * caught up with yet would otherwise bounce between the app and this page as
 * fast as the requests go.
 */

const MAINTENANCE_POLL_MS = 30_000;

function useMaintenanceCheck(audience: NoticeAudience) {
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      syncMaintenanceFromNotices(await fetchPlatformNotices(audience));
      // Over, or down to read-only: whatever failed while the page was away
      // is asked again, so the app comes back with its data rather than with
      // the errors it collected while refused.
      if (getMaintenance()?.mode !== 'FULL') await queryClient.invalidateQueries();
    } catch {
      // Unreachable is not "over". Ask again on the next tick.
    } finally {
      if (alive.current) setChecking(false);
    }
  }, [audience, queryClient]);

  useEffect(() => {
    const timer = window.setInterval(() => void check(), MAINTENANCE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [check]);

  return { check, checking };
}

export function MaintenanceScreen(
  props:
    | { variant: 'dashboard' }
    | { variant: 'storefront'; storeName: string; logoUrl?: string | null },
) {
  return props.variant === 'dashboard' ? (
    <DashboardMaintenance />
  ) : (
    <StorefrontMaintenance storeName={props.storeName} logoUrl={props.logoUrl ?? null} />
  );
}

function DashboardMaintenance() {
  const t = useT();
  const state = useMaintenance();
  const { check, checking } = useMaintenanceCheck('DASHBOARD');
  const time = maintenanceTime(state?.endsAt ?? null);

  return (
    <div className="grid min-h-dvh place-items-center bg-surface/70 px-4 py-10">
      <main
        role="status"
        className="w-full max-w-lg rounded-3xl border border-line/80 bg-white px-6 py-10 text-center shadow-lift sm:px-10"
      >
        <BrandLogo to={null} size="md" withTagline className="mx-auto mb-6 w-fit" />
        <div className="mx-auto grid size-14 place-items-center rounded-2xl bg-brand-50 text-brand-600">
          <Wrench className="size-7" aria-hidden />
        </div>
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-ink text-balance">
          {time
            ? t('system.maintenance.screen_title_until', { time })
            : t('system.maintenance.screen_title')}
        </h1>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted">
          {t('system.maintenance.screen_body')}
        </p>
        <Button
          variant="outline"
          className="mt-6"
          disabled={checking}
          onClick={() => void check()}
        >
          <RotateCw className="mr-1.5 size-4" aria-hidden />
          {t('system.maintenance.screen_check')}
        </Button>
      </main>
    </div>
  );
}

/**
 * The shop's own face: its logo, its name, its colours (the layout renders
 * this inside the themed element). No platform branding - a shopper does not
 * know what NeonCarts is and should not have to.
 */
function StorefrontMaintenance({ storeName, logoUrl }: { storeName: string; logoUrl: string | null }) {
  const text = useShopContent();
  const state = useMaintenance();
  const { check, checking } = useMaintenanceCheck('STOREFRONT');
  const time = maintenanceTime(state?.endsAt ?? null);

  useEffect(() => {
    const previous = document.title;
    document.title = storeName;
    return () => {
      document.title = previous;
    };
  }, [storeName]);

  return (
    <main
      role="status"
      className="grid min-h-dvh flex-1 place-items-center px-4 py-12 text-center"
      style={{ color: 'var(--storefront-text)' }}
    >
      <div className="w-full max-w-md">
        {logoUrl ? (
          <img src={logoUrl} alt={storeName} className="mx-auto mb-6 h-16 w-auto object-contain" />
        ) : (
          <p className="mb-6 text-xl font-semibold" style={STOREFRONT_HEADING_STYLE}>
            {storeName}
          </p>
        )}
        <h1 className="text-3xl text-balance" style={STOREFRONT_HEADING_STYLE}>
          {text('maintenance.title')}
        </h1>
        <p className="mt-3 text-sm leading-relaxed" style={{ color: 'var(--storefront-muted)' }}>
          {text('maintenance.body')}
        </p>
        <p className="mt-4 text-sm font-medium">
          {time ? text('maintenance.back_by', { time }) : text('maintenance.back_shortly')}
        </p>
        <button
          type="button"
          disabled={checking}
          onClick={() => void check()}
          className="mt-6 inline-flex h-11 items-center justify-center gap-2 px-6 text-sm font-semibold disabled:opacity-60"
          style={STOREFRONT_BUTTON_STYLE}
        >
          <RotateCw className="size-4" aria-hidden />
          {text('maintenance.retry')}
        </button>
      </div>
    </main>
  );
}
